import * as THREE from 'three';

// Type pour les options de snapshot
interface SnapshotOptions {
	width?: number;
	height?: number;
	format?: 'png' | 'jpg' | 'jpeg';
	quality?: number; // pour jpg/jpeg uniquement
	preserveDrawingBuffer?: boolean;
}

export class CameraSnapshotManager {
	private renderer: THREE.WebGLRenderer;
	private camera: THREE.Camera;
	private scene: THREE.Scene;

	constructor(
		renderer: THREE.WebGLRenderer,
		camera: THREE.Camera,
		scene: THREE.Scene
	) {
		this.renderer = renderer;
		this.camera = camera;
		this.scene = scene;
	}

	/**
	 * Prend un snapshot de la vue actuelle de la caméra
	 * @param options Options pour configurer le snapshot
	 * @returns Une promesse qui résout avec un DataURL de l'image capturée
	 */
	takeSnapshot(options: SnapshotOptions = {}): Promise<string> {
		return new Promise((resolve) => {
			// Options par défaut
			const defaultOptions: Required<SnapshotOptions> = {
				width: this.renderer.domElement.width,
				height: this.renderer.domElement.height,
				format: 'png',
				quality: 0.92,
				preserveDrawingBuffer: false
			};

			const config = {...defaultOptions, ...options};

			// Créer un renderer temporaire pour la capture
			const tempRenderer = new THREE.WebGLRenderer({
				antialias: true,
				alpha: true,
				preserveDrawingBuffer: true
			});

			tempRenderer.setSize(config.width, config.height);
			tempRenderer.setClearColor(0x000000, 0);

			// Créer une scène temporaire
			const tempScene = new THREE.Scene();

			// Cloner les objets essentiels de la scène principale
			// Note: selon la complexité de votre scène, vous pourriez avoir besoin
			// d'une logique de clonage plus sophistiquée ici
			this.scene.children.forEach(child => {
				if (child.visible) {
					tempScene.add(child.clone());
				}
			});

			// Configurer les propriétés d'éclairage et d'environnement
			tempScene.background = this.scene.background;
			tempScene.environment = this.scene.environment;
			tempScene.fog = this.scene.fog;

			// Prendre le snapshot avec le renderer temporaire
			tempRenderer.render(tempScene, this.camera);

			// Convertir le rendu en image
			const dataURL = tempRenderer.domElement.toDataURL(
				`image/${config.format}`,
				config.format === 'png' ? undefined : config.quality
			);

			// Nettoyer les ressources
			tempRenderer.dispose();
			tempScene.clear();

			// Résoudre avec l'URL de données de l'image
			resolve(dataURL);
		});
	}

	/**
	 * Prend un snapshot et le télécharge automatiquement
	 * @param filename Nom du fichier à télécharger
	 * @param options Options pour configurer le snapshot
	 */
	downloadSnapshot(filename: string = 'camera-snapshot', options: SnapshotOptions = {}): void {
		const format = options.format || 'png';
		const fullFilename = `${filename}.${format}`;

		this.takeSnapshot(options).then(dataURL => {
			const link = document.createElement('a');
			link.href = dataURL;
			link.download = fullFilename;
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		});
	}
}
